import { InfoTip } from "@/components/InfoTip";
import { formatDate, yearsAgo } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { Plant } from "@/types/api";

const INSPECTION_01_MAX_YEARS = 5;

interface Props {
  plants: Plant[];
}

export function PlantCard({ plants }: Props): JSX.Element {
  return (
    <section className="card">
      <h2 className="card-title">Plants ({plants.length})</h2>
      <p className="text-xs text-ink-400 mb-3">
        Treatment plants for this system and when each was last inspected.
      </p>
      {plants.length === 0 ? (
        <p className="text-sm text-ink-300">No treatment plants in FDEP's records for this system. AquaTrack mirrors the state's data — if a plant is missing here, the record needs correcting with FDEP, not in AquaTrack. FDEP's plants dataset is also occasionally unavailable; if other cards look current, try Refresh later.</p>
      ) : (
        <ul className="divide-y divide-ink-700">
          {plants.map((p) => {
            const since = yearsAgo(p.last_01_inspection);
            // null = FDEP doesn't publish the date (~85% of plants
            // statewide) — unknown, not overdue (v1.25.0)
            const overdue = since !== null && since > INSPECTION_01_MAX_YEARS;
            return (
              <li key={p.name} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{p.name}</p>
                  <span className="inline-flex items-center">
                    <span className={p.is_active ? "pill-ok" : "pill-medium"}>
                      {p.is_active ? "Active" : "Inactive"}
                    </span>
                    <InfoTip {...TIPS.plantActive} />
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <dt className="text-ink-300 inline-flex items-center">Last inspection<InfoTip {...TIPS.lastInspection} /></dt>
                  <dd>{formatDate(p.last_inspection)}</dd>
                  <dt className="text-ink-300 inline-flex items-center">01 inspection<InfoTip {...TIPS.inspection01} /></dt>
                  <dd className={overdue ? "text-severity-high font-medium" : ""}>
                    {formatDate(p.last_01_inspection)}
                    {overdue && p.last_01_inspection !== null && (
                      <span className="ml-2 text-xs">
                        ({since !== null ? since.toFixed(1) : "—"} y ago)
                      </span>
                    )}
                  </dd>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
